import React, { useEffect, useMemo, useState } from "react";
import { Box, Text } from "@storyofams/react-ui";
import { WithStoryProps, withStory } from "@storyofams/storyblok-toolkit";
import { findIndex } from "lodash";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { useRouter } from "next/router";
import Script from "next/script";
import { usePostHog } from "posthog-js/react";
import { useInView } from "react-intersection-observer";
import { useQuery } from "react-query";
import { DynamicSection } from "~components/common/Story/blocks/Dynamic";
import { CrowdFunding } from "~components/product/CrowdFunding";
import { Icons } from "~components/product/Icons";
import { InfoAccordion } from "~components/product/InfoAccordion";
import { SkinTypes } from "~components/product/SkinTypes";
import { loadProductBundle } from "~lib/loadProductBundle";
import { storyblokSdk } from "~lib/storyblok/client";
import {
  Container,
  DynamicSections,
  HeaderContainer,
  InformationTabs,
  Links,
  ProductSlider,
  ProductStructuredData,
  Reviews,
  SellingPoints,
  Seo,
  ShopifyRichText,
  VariantSelection,
} from "../../components";
import { CallToAction } from "../../components/common/Story/blocks/CallToAction";
import { ReviewsOverview } from "../../components/product/Reviews/Overview";
import {
  enhancedStaticProps,
  formatShopifyIdForTracking,
  getProductClickOrigin,
  loadProducts,
  track,
  withLayout,
} from "../../lib";
import { getShopifySdk, shopifySdk } from "../../lib/shopify/client";
import { ProductByHandleQuery, ProductSortKeys } from "../../lib/shopify/sdk";

interface ProductDetailPageProps extends WithStoryProps {
  product: ProductByHandleQuery["productByHandle"];
  productDetails?: any;
  propertys?: any;
  bundles?: any;
}

const removeHiddenVariants = (prod) => {
  const filteredVariants = prod?.variants?.edges?.filter(({ node }) => {
    const metafieldNode = node?.metafields[0];
    if (metafieldNode?.key === "hide") {
      return metafieldNode?.value !== "true";
    }
    return node?.value !== "true";
  });
  return { ...prod, variants: { ...prod.variants, edges: filteredVariants } };
};

const ProductDetailPage: NextPage<ProductDetailPageProps> = ({
  product,
  story,
  productDetails,
  propertys,
  bundles,
}) => {
  const posthog = usePostHog();
  const { ref, inView } = useInView();
  const { query, replace, isFallback, events, reload } = useRouter();
  const [variantId, setSelectedVariantId] = useState(
    `gid://shopify/ProductVariant/${query?.variantId}` as string
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const noIndex = product.metafields.find(
    (item) => item?.key === "hide" && item?.value === "true"
  );
  const selectedVariant = useMemo(() => {
    if (query?.variantId) {
      const variant = product?.variants?.edges?.find(
        ({ node }) =>
          node.id === `gid://shopify/ProductVariant/${query?.variantId}`
      );

      return variant?.node;
    } else {
      const firstInStock = findIndex(product?.variants?.edges, function (item) {
        return item.node.availableForSale;
      });

      return product?.variants?.edges?.[firstInStock === -1 ? 0 : firstInStock]
        ?.node;
    }
  }, [product, variantId, query?.variantId]);
  const scrollToReviewsSection = (cb = null) => {
    const el = document?.getElementById("reviews");

    replace(`/products/${product.handle}`, undefined, {
      shallow: true,
    });

    if (el) {
      el.scrollIntoView({ behavior: "smooth" });

      if (cb) {
        cb();
      }
    }
  };
  // useEffect(() => {
  //   posthog?.capture("$pageview");
  // }, [product.id]);
  const submitReviewFromEmail = async () => {
    // const { name, email, rate, body } = query;
    // if (name && email && rate && body && product?.id) {
    //   const review = await createReviewStamped({
    //     name,
    //     email,
    //     rate,
    //     body,
    //     product_id: product?.id,
    //   });
    //   if (review) {
    //     setIsSubmitted(true);
    //     scrollToReviewsSection();
    //   }
    // }
  };
  useEffect(() => {
    if (query?.section === "reviews" && !isScrolled) {
      scrollToReviewsSection(() => setIsScrolled(true));
      if (query?.submit === "true" && !isSubmitted) {
        submitReviewFromEmail();
      }
    }
  }, [query?.section, query?.submit]);
  useEffect(() => {
    const el = document?.getElementById("reviews");
    if (el && query.tab === "reviews") {
      setTimeout(
        () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
        1000
      );
    }
  }, [query?.tab]);
  useEffect(() => {
    const handleRouteChange = (url) => {
      if (!url.includes(query.handle)) {
        reload();
      }
    };
    events.on("routeChangeComplete", handleRouteChange);
    return () => {
      events.off("routeChangeComplete", handleRouteChange);
    };
  }, [query]);
  useEffect(() => {
    if (!variantId || variantId !== selectedVariant?.id) {
      setSelectedVariantId(selectedVariant?.id);
    }
    track({
      event: "dl_view_item",
      ecommerce: {
        currencyCode: "EUR",
        detail: {
          actionField: {
            list: getProductClickOrigin(formatShopifyIdForTracking(product.id)),
          },
          products: [
            {
              name: product.title || "",
              id: selectedVariant?.sku || "",
              product_id: formatShopifyIdForTracking(product.id),
              variant_id: formatShopifyIdForTracking(selectedVariant?.id) || "",
              image: selectedVariant?.image?.originalSrc || "",
              price: selectedVariant?.priceV2.amount,
              brand: product.vendor || "",
              variant: selectedVariant?.title || "",
              category: product.productType,
              list: getProductClickOrigin(
                formatShopifyIdForTracking(product.id)
              ),
              compare_at_price: String(
                selectedVariant?.compareAtPriceV2?.amount
              ),
              inventory: String(selectedVariant?.quantityAvailable),
            },
          ],
        },
      },
    });
  }, [selectedVariant]);

  const { data: productRecommendations } = useQuery(
    ["productRecommendations", product?.id, query?.page],
    async () => {
      const products = await shopifySdk.productRecommendations({
        productId: `${product?.id}`,
      });
      return products.productRecommendations.filter(
        //@ts-ignore
        ({ hide }) => hide.value !== "true"
      );
    },
    {
      enabled: !!product?.id,
      refetchOnWindowFocus: false,
    }
  );
  const { data: productComplementary } = useQuery(
    ["productComplementary", product?.id, query?.page],
    () =>
      shopifySdk.productComplementary({
        productId: `${product?.id}`,
      }),
    {
      enabled: !!product?.id,
      refetchOnWindowFocus: false,
    }
  );
  // const { data: blogPosts } = useQuery(
  //   ["blogItemsProduct", product?.id, query?.page],
  //   () => storyblokSdk.blogItems({ perPage: 3 }),
  //   {
  //     enabled: !!product?.id,
  //     refetchOnWindowFocus: false,
  //   }
  // );
  if (isFallback) return null;
  const { content } = productDetails || {};
  const description = product?.metafields?.find(
    (item) => item?.key === "description_excerpt"
  )?.value;
  return (
    <>
      <Script
        dangerouslySetInnerHTML={{
          __html: `var SKIP_CART=true;
            var FORCE_DOMAIN = true;
            var VIDEOWISE_FAST_INLINE_VIDEO_PLAYER = true;
            var Shopify = { 
              shop: 'natural-heroes-nl.myshopify.com', currency:{active:'EUR', rate:'1.0'}
            };

            var __st = {
              rid: ${formatShopifyIdForTracking(product.id)},
              p: 'product'
            };`,
        }}
      />
      <Script
        defer
        src="https://assets.videowise.com/vendors.js.gz"
        id="videowise-vendors-js"
      />
      <Script
        defer
        src="https://assets.videowise.com/client.js.gz"
        id="videowise-client-js"
      />
      <Seo
        noIndex={story?.content.noIndex || noIndex}
        title={product?.seo?.title || product.title}
        defaultTemplate
        description={product?.seo?.description || product.description}
        image={product?.images?.edges?.[0]?.node?.image?.originalSrc}
        canonical={`https://www.naturalheroes.nl/products/${product.handle}`}
      />
      <ProductStructuredData product={product} sku={selectedVariant?.sku} />

      <HeaderContainer
        videoEmbed={story?.content?.video_embed}
        videoEmbedPreview={story?.content?.video_embed_preview}
        embedPreview={story?.content?.embed_thumbnail}
        position={"relative"}
        product={product}
        variant={selectedVariant}
        isReviewsDisabled={!story?.content?.isReviewsDisabled}
      >
        <Box display={"flex"} flexDirection={"column"}>
          <Box display={["none", "block"]}>
            <Text
              fontFamily="DINPro"
              fontWeight={700}
              fontSize={["40px", "56px"]}
              mb={["16px"]}
              data-id={"title"}
            >
              {product?.title}
            </Text>
            {!story?.content?.isReviewsDisabled && (
              <ReviewsOverview id={product?.id} />
            )}
          </Box>
        </Box>
        <ShopifyRichText
          mb={product?.campaignEnabled?.value === "true" ? "25px" : 2}
          fontSize={["16px", "18px"]}
          content={description}
        />
        <SkinTypes product={product} />
        <InfoAccordion
          content={[
            ...(story?.content?.accordions ? story?.content?.accordions : []),
            {
              component: "product_choice",
              content: story?.content?.product_choice?.length
                ? story.content.product_choice
                : null,
              resolvedProducts: story?.content?.resolvedProducts,
            },
            {
              component: "bundle",
              content: product.variants.edges[0].node.components.nodes.length
                ? product.variants.edges[0].node.components.nodes
                : null,
            },
          ]}
        />
        {story?.content?.isIconsEnabled && (
          <Icons content={story?.content.icons} />
        )}
        {product?.campaignEnabled?.value === "true" ? (
          <CrowdFunding
            product={product}
            content={story?.content}
            productRecommendations={
              productComplementary?.productRecommendations?.length
                ? productComplementary?.productRecommendations
                : productRecommendations
            }
          />
        ) : (
          <VariantSelection
            content={story?.content}
            isUpsellDisabled={story?.content.isUpsellDisabled}
            tiered_discount_enabler={content.tiered_discount_enabler}
            product={product}
            productRecommendations={
              productComplementary?.productRecommendations?.length
                ? productComplementary?.productRecommendations
                : productRecommendations
            }
            mb={["20px", "30px"]}
          />
        )}
        <Links links={content?.links} hasBundles={!!bundles} />

        <SellingPoints items={content?.usps} />
      </HeaderContainer>
      {bundles && !!bundles?.length && (
        <Container
          withOverflowHidden
          maxWidth={"1332px"}
          px={2}
          space={[8, 112]}
          pb={[2, 5]}
          pt={[2, 5]}
        >
          <ProductSlider
            centered={false}
            title={"Koop als bundel en bespaar"}
            description={product.title + " is onderdeel van deze bundels"}
            isBundles
            products={bundles as any}
          />
        </Container>
      )}
      <Container pt={0} px={2} pb={0} withOverflowHidden space={[3, 3]}>
        {story?.content?.components && (
          <DynamicSections sections={story?.content.components} />
        )}
        {story?.content.before_description && (
          <DynamicSections sections={story?.content.before_description[0]} />
        )}
        {!story?.content?.isTabsDisabled && (
          <InformationTabs
            product={product}
            isDescriptionDisabled={story?.content.isDescriptionDisabled}
            isIngredientsDisabled={story?.content?.IsIngredientsDisabled}
          />
        )}
        {story?.content.after_description && (
          <DynamicSections sections={story?.content.after_description[0]} />
        )}
        <Box ref={ref} />
        {!story?.content && (
          <Box
            mt="32px"
            width={"100%"}
            height={"100%"}
            // onClick={() => posthog.capture("Clicked Greenspark Homepage")}
            dangerouslySetInnerHTML={{
              __html: `<div
            class="reeview-app-widget"
            id="reeview-app-widget_671a7388fef448002a0b2f83"
          ></div>`,
            }}
          />
        )}
        {!story?.content?.isReviewsDisabled && (
          <Box id="reviews" width="100%" css={{ "scroll-margin-top": "100px" }}>
            <Reviews
              propertys={propertys}
              productId={product?.id}
              productTitle={product?.title}
            />
          </Box>
        )}
        {!story?.content.isProductRecomendationsDisabled && (
          <ProductSlider
            mb={[2, 8]}
            title={content?.recommendations_title}
            description={content?.recommendations_description}
            products={productRecommendations as any}
            link="/products"
          />
        )}
      </Container>

      <CallToAction
        footerColor={story?.content?.footer_color?.color}
        content={content?.cta2?.content}
        mb={[10, 5]}
      />
    </>
  );
};

export default withLayout(withStory(ProductDetailPage));

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    fallback: "blocking",
    paths: [],
  };
};

export const getStaticProps: GetStaticProps<ProductDetailPageProps> =
  enhancedStaticProps(async ({ params, sdk }) => {
    const { handle } = params;
    const shopifySdk = getShopifySdk();
    let product;
    let notFound;
    let story;
    let propertys;
    let bundles = null;

    try {
      product = (await shopifySdk.productByHandle({ handle: `${handle}` }))
        .productByHandle;
    } catch (err) {
      console.log(err);
    }
    if (!product) {
      notFound = true;

      return {
        props: {},
        notFound: true,
      };
    }
    const pureProduct = removeHiddenVariants(product);
    if (!pureProduct.variants?.edges?.length) {
      notFound = true;

      return {
        props: {},
        notFound: true,
      };
    }
    try {
      story = (
        await sdk.productDetailsItem({
          slug: `products/${String(handle)}`,
        })
      )?.ProductdetailsItem;
    } catch (err) {
      console.log(err);
    }
    try {
      propertys = (await storyblokSdk.userProperys({ slug: "user-propertys" }))
        ?.UserpropertysItem.content;
    } catch (err) {
      console.log(err);
    }
    const [{ ProductdetailItem }] = await Promise.all([
      sdk.pdp({ slug: "/products/product-detail" }),
    ]);
    try {
      bundles = await loadProductBundle(product);
    } catch (err) {
      console.log(err);
    }
    try {
      await loadProducts(story, "nl");
    } catch {}

    return {
      props: {
        product: pureProduct,
        story: story || null,
        productDetails: ProductdetailItem,
        propertys,
        bundles,
      },
      notFound,
      revalidate: process.env.HOST === "dev.naturalheroes.nl" ? 1 : 7200,
    };
  });
